/**
 * Authenticated seller marketplace API (Treydora.com → TreydoraEdBackend).
 */
import { getApiBaseUrl } from "../utils/getApiBaseUrl";
import { authHeaders, getAuthToken } from "./authApi";

const API_BASE_URL = getApiBaseUrl();

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

export function marketplaceSellerRequiresLogin() {
  return !getAuthToken();
}

export async function fetchMyMarketplaceSellerContext() {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (res.status === 401 || res.status === 403) {
    const err = new Error(body.message || "Please log in.");
    err.code = "AUTH";
    throw err;
  }
  if (!res.ok) {
    throw new Error(body.message || "Unable to load your storefront.");
  }
  return body.data || {};
}

export async function createMarketplaceStore(payload) {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/stores`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || "Could not create storefront.");
    err.code = body.code;
    throw err;
  }
  return body.data || {};
}

export async function patchMyMarketplaceStore(payload) {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(body.message || "Could not update store.");
  }
  return body.data || {};
}

export async function submitMyStoreForReview(storeId) {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store/submit-review`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(storeId ? { storeId } : {}),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(body.message || "Could not submit for review.");
  }
  return body.data || {};
}

/** @param {{ storeId?: string }} [params] */
export async function fetchMyStoreProducts(params = {}) {
  const sp = new URLSearchParams();
  if (params.storeId) sp.set("storeId", params.storeId);
  const qs = sp.toString() ? `?${sp.toString()}` : "";
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store/products${qs}`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(body.message || "Could not load products.");
  }
  return body.data?.products ?? [];
}

export async function fetchMyStoreProduct(productId) {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store/products/${encodeURIComponent(productId)}`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (!res.ok) {
    throw new Error(body.message || "Could not load product.");
  }
  return body.data?.product ?? null;
}

export async function createMyStoreProductRequest(payload) {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store/products`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || "Could not create product.");
    err.code = body.code;
    throw err;
  }
  return body.data?.product ?? null;
}

export async function patchMyStoreProductRequest(productId, payload) {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store/products/${encodeURIComponent(productId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || "Could not update product.");
    err.code = body.code;
    throw err;
  }
  return body.data?.product ?? null;
}

export async function submitMyStoreProductForReview(productId) {
  const res = await fetch(
    `${API_BASE_URL}/api/marketplace/my-store/products/${encodeURIComponent(productId)}/submit-review`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({}),
    }
  );
  const body = await parseJson(res);
  if (!res.ok) {
    const err = new Error(body.message || "Could not submit for review.");
    err.code = body.code;
    throw err;
  }
  return body.data?.product ?? null;
}

/** @returns {Promise<object[]>} */
export async function fetchMyStoreOrders() {
  const res = await fetch(`${API_BASE_URL}/api/marketplace/my-store/orders`, {
    headers: { ...authHeaders() },
  });
  const body = await parseJson(res);
  if (res.status === 401 || res.status === 403) {
    const err = new Error(body.message || "Please log in.");
    err.code = "AUTH";
    throw err;
  }
  if (!res.ok) {
    throw new Error(body.message || "Could not load orders.");
  }
  return body.data?.orders ?? [];
}
